import { type FormEvent, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { THEMES } from '@tigrinho/engine';
import type { GameEvent } from '@tigrinho/engine';
import type { ClientAction, ClientMsg, PublicRoom, RoomInfo, ServerMsg } from '@tigrinho/server/protocol';
import { navigate } from '../App';
import { money as fmtMoney } from '../lib/format';
import { saveName, savedName } from '../lib/storage';
import { type RoomConn, useRoom } from '../net/useRoom';
import { Game } from './Game';
import { Lobby } from './Lobby';

/** Tudo que as telas da sala precisam. */
export interface RoomCtx {
  room: PublicRoom;
  you: string | null;
  conn: RoomConn;
  act: (a: ClientAction) => void;
  send: (m: ClientMsg) => void;
  /** Agora, no relógio do servidor. */
  serverNow: () => number;
  name: (id: string) => string;
  seatIndex: (id: string) => number;
  square: (i: number) => string;
  money: (n: number) => string;
  bus: EventBus;
  /** Dados rolando na tela (o resultado ainda não apareceu). */
  rolling: { player: string; at: number } | null;
  /** Até quando a tela mostra o peão andando: janelas, cartas e placares esperam. */
  holdUntil: number;
  /** "Sorteando quem começa…" na tela (início da partida). */
  ordering: number | null;
}

type Handler<T> = (x: T) => void;

/** Canal interno para animações (eventos do jogo, provocações e frases). */
export class EventBus {
  private ev = new Set<Handler<{ events: GameEvent[]; room: PublicRoom }>>();
  private tt = new Set<Handler<Extract<ServerMsg, { t: 'taunt' }>>>();
  private ph = new Set<Handler<Extract<ServerMsg, { t: 'phrase' }>>>();
  private rl = new Set<Handler<string>>();
  onRoll(h: Handler<string>) {
    this.rl.add(h);
    return () => void this.rl.delete(h);
  }
  emitRoll(player: string) {
    for (const h of this.rl) h(player);
  }
  onEvents(h: Handler<{ events: GameEvent[]; room: PublicRoom }>) {
    this.ev.add(h);
    return () => void this.ev.delete(h);
  }
  onTaunt(h: Handler<Extract<ServerMsg, { t: 'taunt' }>>) {
    this.tt.add(h);
    return () => void this.tt.delete(h);
  }
  onPhrase(h: Handler<Extract<ServerMsg, { t: 'phrase' }>>) {
    this.ph.add(h);
    return () => void this.ph.delete(h);
  }
  emitEvents(x: { events: GameEvent[]; room: PublicRoom }) {
    for (const h of this.ev) h(x);
  }
  emitTaunt(x: Extract<ServerMsg, { t: 'taunt' }>) {
    for (const h of this.tt) h(x);
  }
  emitPhrase(x: Extract<ServerMsg, { t: 'phrase' }>) {
    for (const h of this.ph) h(x);
  }
}

const Ctx = createContext<RoomCtx | null>(null);

/** Verdadeiro enquanto a tela ainda mostra o peão andando (e re-renderiza quando acabar). */
export function useHold(): boolean {
  const { holdUntil } = useRoomCtx();
  const [, force] = useState(0);
  const left = holdUntil - Date.now();
  useEffect(() => {
    if (left <= 0) return;
    const t = setTimeout(() => force((x) => x + 1), left + 10);
    return () => clearTimeout(t);
  }, [left]);
  return left > 0;
}

export function useRoomCtx(): RoomCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('fora da sala');
  return c;
}

export function RoomScreen({ code }: { code: string }) {
  const [name, setName] = useState(savedName());
  if (!name) return <NameGate code={code} onName={setName} />;
  return <Connected code={code} name={name} />;
}

function NameGate({ code, onName }: { code: string; onName: (n: string) => void }) {
  const [value, setValue] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = value.trim();
    if (!v) return;
    saveName(v);
    onName(v);
  };
  return (
    <main className="center-screen">
      <form className="home-card" onSubmit={submit}>
        <p className="kicker-code">Mesa {code}</p>
        <h1 className="screen-title-text">Como a galera te chama?</h1>
        <label className="field">
          <span>Seu apelido</span>
          <input value={value} onChange={(e) => setValue(e.target.value)} maxLength={16} autoFocus autoComplete="nickname" />
        </label>
        <button className="pill pill-blue pill-block" disabled={!value.trim()}>
          Entrar na mesa
        </button>
      </form>
    </main>
  );
}

function Connected({ code, name }: { code: string; name: string }) {
  const [claim, setClaim] = useState<string | null>(null);
  const busRef = useRef(new EventBus());
  const conn = useRoom(code, name, claim, {
    onEvents: (events, room) => busRef.current.emitEvents({ events, room }),
    onTaunt: (m) => busRef.current.emitTaunt(m),
    onPhrase: (m) => busRef.current.emitPhrase(m),
    onRoll: (p) => busRef.current.emitRoll(p),
  });
  const { you, error, offset } = conn;
  // Só no computador de desenvolvimento (127.0.0.1): os testes com prints podem simular
  // eventos e decisões raras (Faz o L, MST) sem jogar 12 rodadas.
  const [preview, setPreview] = useState<((r: PublicRoom) => PublicRoom) | null>(null);
  const room = conn.room && preview ? preview(conn.room) : conn.room;
  useEffect(() => {
    if (location.hostname !== '127.0.0.1' && location.hostname !== 'localhost') return;
    (window as unknown as { __tt: unknown }).__tt = {
      emit: (events: GameEvent[]) => conn.room && busRef.current.emitEvents({ events, room: conn.room }),
      patch: (fn: ((r: PublicRoom) => PublicRoom) | null) => setPreview(() => fn),
    };
  }, [conn.room]);

  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const serverNow = useCallback(() => Date.now() + offsetRef.current, []);

  const ctx = useMemo<RoomCtx | null>(() => {
    if (!room) return null;
    const theme = THEMES[room.settings.theme];
    const names = new Map(room.seats.map((s) => [s.id, s.name]));
    const seatIdx = new Map(room.seats.map((s, i) => [s.id, i]));
    return {
      room,
      you,
      conn,
      act: conn.act,
      send: conn.send,
      serverNow,
      name: (id) => names.get(id) ?? '?',
      seatIndex: (id) => seatIdx.get(id) ?? 0,
      square: (i) => theme.squares[i] ?? `Casa ${i}`,
      money: (n) => fmtMoney(n),
      bus: busRef.current,
      rolling: conn.rolling,
      holdUntil: conn.holdUntil,
      ordering: conn.ordering,
    };
  }, [room, you, conn, serverNow]);

  if (error?.code === 'game_in_progress' || error?.code === 'seat_taken' || error?.code === 'bad_claim') {
    return <ClaimSeat code={code} failed={error.code !== 'game_in_progress'} onClaim={setClaim} />;
  }
  if (error && ['room_full', 'removed', 'room_closed'].includes(error.code)) {
    return <Dead title={error.code === 'room_full' ? 'Mesa cheia' : 'Fora da mesa'} line={error.message} />;
  }
  if (!ctx) {
    return <Loading code={code} status={conn.status} />;
  }
  return (
    <Ctx.Provider value={ctx}>
      {ctx.room.status === 'lobby' ? <Lobby /> : <Game />}
      {conn.status === 'reconnecting' && (
        <div className="net-banner" role="status">
          Conexão caiu. Reconectando…
        </div>
      )}
      <ErrorToast />
    </Ctx.Provider>
  );
}

function ErrorToast() {
  const { conn } = useRoomCtx();
  const e = conn.error;
  useEffect(() => {
    if (!e) return;
    const t = setTimeout(conn.clearError, 3500);
    return () => clearTimeout(t);
  }, [e, conn.clearError]);
  if (!e) return null;
  return (
    <div className="toast toast-error" role="alert" onClick={conn.clearError}>
      {e.message}
    </div>
  );
}

function Loading({ code, status }: { code: string; status: string }) {
  const [info, setInfo] = useState<RoomInfo | null>(null);
  useEffect(() => {
    fetch(`/api/rooms/${code}`)
      .then((r) => r.json() as Promise<RoomInfo>)
      .then(setInfo)
      .catch(() => undefined);
  }, [code]);
  if (info && !info.exists) return <Dead title="Mesa não encontrada" line={`Não existe nenhuma mesa ${code}. Confira o código com quem te convidou.`} />;
  return (
    <main className="center-screen">
      <div className="loading">
        <p className="kicker-code">Mesa {code}</p>
        <p>{status === 'reconnecting' ? 'Sem conexão. Tentando de novo…' : 'Entrando na mesa…'}</p>
      </div>
    </main>
  );
}

function ClaimSeat({ code, failed, onClaim }: { code: string; failed: boolean; onClaim: (id: string) => void }) {
  const [info, setInfo] = useState<RoomInfo | null>(null);
  useEffect(() => {
    fetch(`/api/rooms/${code}`)
      .then((r) => r.json() as Promise<RoomInfo>)
      .then(setInfo)
      .catch(() => undefined);
  }, [code, failed]);
  const seats = info?.claimable ?? [];
  return (
    <main className="center-screen">
      <section className="home-card">
        <p className="kicker-code">Mesa {code}</p>
        <h1 className="screen-title-text">Essa partida já começou</h1>
        {seats.length > 0 ? (
          <>
            <p className="muted">Se você estava jogando em outro aparelho, toque no seu nome para continuar daqui.</p>
            <div className="claim-list">
              {seats.map((s) => (
                <button key={s.id} className="pill pill-yellow pill-block" onClick={() => onClaim(s.id)}>
                  Sou {s.name}
                </button>
              ))}
            </div>
            {failed && <p className="form-error">Esse jogador acabou de voltar em outro aparelho.</p>}
          </>
        ) : (
          <p className="muted">Todos os jogadores estão conectados. Espere a próxima partida ou crie uma mesa nova.</p>
        )}
        <button className="pill pill-gray pill-block" onClick={() => navigate('/')}>
          Voltar ao início
        </button>
      </section>
    </main>
  );
}

function Dead({ title, line }: { title: string; line: string }) {
  return (
    <main className="center-screen">
      <section className="home-card">
        <h1 className="screen-title-text">{title}</h1>
        <p className="muted">{line}</p>
        <button className="pill pill-blue pill-block" onClick={() => navigate('/')}>
          Voltar ao início
        </button>
      </section>
    </main>
  );
}
